import { useTranslation } from 'core/i18n/i18n';
import React from 'react';
import { AutomationRulesDropdown } from '../components/automation-rules-dropdown.component';
import { FieldConditional } from '../components/field-conditional';
import { AutomationRulesDataKeys, RuleActionType } from '../constants/backend-enums';
import { TRANSL_STRG_AUT_RULES } from '../constants/common';
import { FieldConditions, OnChangeStateTypeValues } from '../constants/frontend-enums';
import { FieldsContainer } from '../styled/automation-rules.style';
import {
    ActionsExternalOptions,
    AssignLocationAction,
    EnrollTrainingAction,
    OnChangeAutomationRuleStateData,
    RuleAction,
} from '../types/automation-rules.type';

const ACTIONS_CONDITIONS_KEYS = Object.keys(RuleActionType);
const ACTIONS_CONDITIONS_VALUES = Object.values(RuleActionType);

const ACTIONS_CONDITIONS_TYPES = ACTIONS_CONDITIONS_KEYS.map((el, index) => {
    const value = ACTIONS_CONDITIONS_VALUES[index];
    return { id: value, value, label: TRANSL_STRG_AUT_RULES + el };
});

interface ConditionalActionsContainerProps {
    id?: number;
    fieldsData?: RuleAction;
    externalOptions?: ActionsExternalOptions;
    onChange: (data: OnChangeAutomationRuleStateData) => void;
}

export const ConditionalActionsContainer = (props: ConditionalActionsContainerProps) => {
    const { id, fieldsData, externalOptions, onChange } = props;

    const { t } = useTranslation();

    const { data: fieldData, type: fieldType } = fieldsData || {};

    const [selectedCategory, setSelectedCategory] = React.useState<string>(fieldType ?? '');

    const onChangeHandler = (data: OnChangeAutomationRuleStateData) => {
        const { value } = data;

        data.index = id;
        onChange(data);

        if (typeof value === 'string' && ACTIONS_CONDITIONS_VALUES.includes(value as RuleActionType)) {
            setSelectedCategory(value);
        }
    };

    const secondaryFixedData: Record<string, { props: Record<string, unknown> }> = {
        [RuleActionType.assign_location]: {
            props: {
                options: externalOptions?.[RuleActionType.assign_location] || [],
                placeholder: t(`${TRANSL_STRG_AUT_RULES}choose_locations`),
                fieldCondition: FieldConditions.multipleSelect,
                type: OnChangeStateTypeValues.keep_value,
                defaultSelectedOptions:
                    selectedCategory === RuleActionType.assign_location
                        ? (fieldData as AssignLocationAction)?.locations || []
                        : [],
            },
        },
        [RuleActionType.enroll_training_program]: {
            props: {
                options: externalOptions?.[RuleActionType.enroll_training_program] || [],
                placeholder: t(`${TRANSL_STRG_AUT_RULES}choose_training_program`),
                fieldCondition: FieldConditions.singleSelect,
                type: OnChangeStateTypeValues.keep_value,
                defaultSelectedOption:
                    selectedCategory === RuleActionType.enroll_training_program
                        ? (fieldData as EnrollTrainingAction)?.enrollmentsData?.[0]?.trainingStageId
                        : undefined,
                virtualized: (externalOptions?.[RuleActionType.enroll_training_program] || []).length > 50,
            },
        },
    };

    const secondaryData = secondaryFixedData?.[selectedCategory]?.props || {};

    return (
        <>
            <FieldsContainer>
                <AutomationRulesDropdown
                    name={AutomationRulesDataKeys.type}
                    options={ACTIONS_CONDITIONS_TYPES}
                    placeholder={t(`${TRANSL_STRG_AUT_RULES}choose_action`)}
                    type={OnChangeStateTypeValues.keep_value}
                    onChange={onChangeHandler}
                    defaultSelectedOption={fieldType}
                />
            </FieldsContainer>
            {selectedCategory && (
                <FieldConditional name={AutomationRulesDataKeys.data} onChange={onChangeHandler} {...secondaryData} />
            )}
        </>
    );
};
